import {AddressMode, HalContext, HalImage, HalPainterImageCompositorRef} from "@cm/hal"
import {assertNever} from "@cm/utils"
import {Box2, Box2Like, Vector2, Vector2Like} from "@cm/math"
import {z} from "zod/v4-mini"
import {getHalAddressMode} from "@cm/webgl2"
import {ReductionHelperHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/reduction-helper-hal"
import {HalRef} from "@cm/hal/hal-ref"

export const interpolationMode = z.union([z.literal("nearest"), z.literal("cubic"), z.literal("lanczos2"), z.literal("lanczos3")])

export type InterpolationMode = z.infer<typeof interpolationMode>

export class ResizeHelperHal {
    constructor(private halContext: HalContext) {
        this.reducionHelper = new ReductionHelperHal(halContext, {operator: "mean"})
    }

    dispose() {
        this.painterByInterpolationMode.forEach((painter) => painter.release())
        this.painterByInterpolationMode.clear()
        this.reducionHelper.dispose()
    }

    getHaloWidth(interpolationMode: InterpolationMode) {
        switch (interpolationMode) {
            case "nearest":
                return 0
            case "cubic":
                return 2
            case "lanczos2":
                return 2
            case "lanczos3":
                return 3
            default:
                assertNever(interpolationMode)
        }
    }

    resize(args: {
        source: HalImage
        target: HalImage
        interpolationMode: InterpolationMode
        sourceMipLevel?: number
        sourceRegion?: Box2Like
        targetMipLevel?: number
        targetRegion?: Box2Like
        clipRegion?: Box2Like
        addressMode?: AddressMode // default: "clamp"
    }) {
        let painter = this.painterByInterpolationMode.get(args.interpolationMode)
        if (!painter) {
            painter = this.createCompositor(args.interpolationMode)
            this.painterByInterpolationMode.set(args.interpolationMode, painter)
        }

        // DOWN-SAMPLING PASS
        const targetMipLevel = args.targetMipLevel ?? 0
        let sourceMipLevel = args.sourceMipLevel ?? 0
        if (targetMipLevel < 0 || targetMipLevel >= args.target.descriptor.numMipLevels) {
            throw new Error(`Invalid target mip level: ${targetMipLevel}`)
        }
        if (sourceMipLevel < 0 || sourceMipLevel >= args.source.descriptor.numMipLevels) {
            throw new Error(`Invalid source mip level: ${sourceMipLevel}`)
        }

        const targetMipLevelScale = 1 / (1 << targetMipLevel)
        const targetRegion = args.targetRegion ?? {
            x: 0,
            y: 0,
            width: args.target.width * targetMipLevelScale,
            height: args.target.height * targetMipLevelScale,
        }
        const sourceMipLevelScale = 1 / (1 << sourceMipLevel)
        const sourceRegion = args.sourceRegion ?? {
            x: 0,
            y: 0,
            width: args.source.width * sourceMipLevelScale,
            height: args.source.height * sourceMipLevelScale,
        }

        const effectiveImageSize = new Vector2(sourceRegion.width, sourceRegion.height)
        // const sourceMipLevelBias = sourceMipLevel - targetMipLevel
        // const sourceMipLevelBiasScale = Math.pow(2, -sourceMipLevelBias)
        // effectiveImageSize.mulInPlace(sourceMipLevelBiasScale)

        // shrink the image by an integer factors (to be at least the target size times the shrinkMinHeadroomScale)
        // NOTE: even though lib-vips says this headroom should be at least 2x the target resolution, practically it has been observed that this can still cause severe moiré patterns (e.g. see "Baltimore Black" - Specular Strength map)
        const shrinkMinHeadroomScale = 1 // the down-sampled (pre-filtered) resolution will be between the target size and twice the target size before final resample
        const shrinkFactor = {
            x: Math.max(1, Math.floor(sourceRegion.width / targetRegion.width / shrinkMinHeadroomScale)),
            y: Math.max(1, Math.floor(sourceRegion.height / targetRegion.height / shrinkMinHeadroomScale)),
        }
        let sourceImage: HalRef<HalImage>
        let sourceRegionOffset: Vector2Like
        if (shrinkFactor.x > 1 || shrinkFactor.y > 1) {
            effectiveImageSize.divInPlace(shrinkFactor)
            const clipRegion = args.clipRegion ?? targetRegion
            const sourceRegionScaleX = sourceRegion.width / targetRegion.width
            const sourceRegionScaleY = sourceRegion.height / targetRegion.height
            const x0 = Math.floor(clipRegion.x * sourceRegionScaleX)
            const y0 = Math.floor(clipRegion.y * sourceRegionScaleY)
            const x1 = Math.ceil((clipRegion.x + clipRegion.width) * sourceRegionScaleX)
            const y1 = Math.ceil((clipRegion.y + clipRegion.height) * sourceRegionScaleY)
            const sourceClipRegion = {
                x: x0 + targetRegion.x - sourceRegion.x,
                y: y0 + targetRegion.y - sourceRegion.y,
                width: x1 - x0,
                height: y1 - y0,
            }
            const clippedSourceRegion = Box2.intersect(sourceClipRegion, sourceRegion)
            const sourceMipLevelView = args.source.createView({
                type: "sub-image",
                region: Box2.expandToIntegers(clippedSourceRegion),
                mipLevelOffset: sourceMipLevel,
            })
            const reducedSource = this.halContext.createPhysicalImage({
                width: Math.ceil(clippedSourceRegion.width / shrinkFactor.x),
                height: Math.ceil(clippedSourceRegion.height / shrinkFactor.y),
                channelLayout: args.source.descriptor.channelLayout,
                dataType: args.source.descriptor.dataType,
            })
            this.reducionHelper.reduce({
                source: sourceMipLevelView,
                target: reducedSource.ref,
                options: {
                    numPatches: {x: reducedSource.ref.descriptor.width, y: reducedSource.ref.descriptor.height},
                    patchSize: {width: shrinkFactor.x, height: shrinkFactor.y},
                    patchFillAddressMode: "border",
                },
            })
            sourceImage = reducedSource
            sourceRegionOffset = {
                x: -clippedSourceRegion.x / shrinkFactor.x,
                y: -clippedSourceRegion.y / shrinkFactor.y,
            }
            sourceMipLevel = 0
        } else {
            sourceImage = new HalRef(args.source)
            sourceRegionOffset = {
                x: sourceRegion.x,
                y: sourceRegion.y,
            }
        }

        // RESAMPLING PASS
        //if (args.resultSize.width !== effectiveImageSize.x || args.resultSize.height !== effectiveImageSize.y || sourceMipLevelBias !== 0) {
        const texelStep = {
            x: effectiveImageSize.x / targetRegion.width,
            y: effectiveImageSize.y / targetRegion.height,
        }
        const sourceOffset = {
            x: sourceRegionOffset.x - targetRegion.x * texelStep.x,
            y: sourceRegionOffset.y - targetRegion.y * texelStep.y,
        }
        painter.ref.paint({
            parameters: {
                u_sourceOffset: {
                    type: "float2",
                    value: sourceOffset,
                },
                u_sourceMipLevel: {
                    type: "int",
                    value: sourceMipLevel,
                },
                u_texelStep: {
                    type: "float2",
                    value: texelStep,
                },
                u_addressMode: {
                    type: "int",
                    value: getHalAddressMode(args.addressMode ?? "clamp"),
                },
            },
            sourceImages: sourceImage.ref,
            target: args.target,
            options: {
                targetRegion: args.clipRegion,
                targetMipLevel: targetMipLevel,
            },
        })
        // } else {
        //     resultImageOrDataType = sourceImage   // TODO fully instantiate sourceImage and return
        // }
        sourceImage.release()
    }

    private createCompositor(interpolationMode: InterpolationMode) {
        switch (interpolationMode) {
            case "nearest":
                return this.halContext.createHalPainterImageCompositor(
                    `
                        uniform int u_sourceMipLevel;
                        uniform vec2 u_sourceOffset;
                        uniform vec2 u_texelStep;
                        uniform int u_addressMode;

                        vec4 computeColor(ivec2 targetPixel) {
                            vec2 sourcePixel = (vec2(targetPixel) + 0.5) * u_texelStep - 0.5;
                            return texelFetchLod0(ivec2(sourcePixel + 0.5 + u_sourceOffset), u_sourceMipLevel, u_addressMode);
                        }
                    `,
                )
                break
            case "cubic":
                return this.halContext.createHalPainterImageCompositor(
                    `
                        uniform int u_sourceMipLevel;
                        uniform vec2 u_sourceOffset;
                        uniform vec2 u_texelStep;
                        uniform int u_addressMode;

                        // Catmull-Rom spline interpolation function
                        vec4 catmullRom(vec4 P0, vec4 P1, vec4 P2, vec4 P3, float t) {
                            vec4 a0 = P1;
                            vec4 a1 = 0.5 * (P2 - P0);
                            vec4 a2 = 0.5 * (2.0 * P0 - 5.0 * P1 + 4.0 * P2 - P3);
                            vec4 a3 = 0.5 * (3.0 * (P1 - P2) + P3 - P0);
                            float t2 = t * t;
                            float t3 = t2 * t;
                            return a0 + a1 * t + a2 * t2 + a3 * t3;
                        }

                        vec4 computeColor(ivec2 targetPixel) {
                            vec2 sourcePixel = (vec2(targetPixel) + 0.5) * u_texelStep - 0.5 + u_sourceOffset;

                            ivec2 baseCoord = ivec2(floor(sourcePixel));
                            vec2 fractCoord = sourcePixel - vec2(baseCoord);

                            vec4 sampleRows[4];
                            for (int j = -1; j <= 2; j++) {
                                vec4 rowValues[4];
                                for (int i = -1; i <= 2; i++) {
                                    ivec2 offset = ivec2(i, j);
                                    rowValues[i + 1] = texelFetchLod0(baseCoord + offset, u_sourceMipLevel, u_addressMode);
                                }
                                // Interpolate along the X-axis
                                sampleRows[j + 1] = catmullRom(rowValues[0], rowValues[1], rowValues[2], rowValues[3], fractCoord.x);
                            }
                            // Interpolate along the Y-axis using the interpolated rows
                            return catmullRom(sampleRows[0], sampleRows[1], sampleRows[2], sampleRows[3], fractCoord.y);
                        }
                    `,
                )
                break
            case "lanczos2":
            case "lanczos3":
                throw new Error("Resize: lanczos interpolation not implemented yet")
            default:
                assertNever(interpolationMode)
        }
    }

    private readonly painterByInterpolationMode = new Map<InterpolationMode, HalPainterImageCompositorRef>()
    private readonly reducionHelper: ReductionHelperHal
}
